import React, { useEffect } from "react";
import {
	Control,
	Controller,
	ControllerFieldState,
	ControllerProps,
	ControllerRenderProps,
	FieldValues,
	UseControllerProps,
	UseFormStateReturn,
} from "react-hook-form";
import { renderErrorMessages } from "@/main/services/utils";
import { dateInputMask } from "@/main/services/utilsMask";

// LOCAL COMPONENTS/STYLES
import KogInputTextOutlined from "./inputTextOutlined";
import KogInputText from "./inputText";

type IControllerProps = Omit<ControllerProps, "render" | "name">;
type IHTMLInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "number" | "color" | "size">;
interface IDateMaskControlledProps {
	id: string;
	typeInput?: "outlined" | "filled";
	required?: boolean;
	errorMessageCustom?: string;
	controllerProps?: IControllerProps;
	label?: string;
	customInputProps?: IHTMLInputProps;
	disabledMessage?: boolean;
	hideValue?: boolean;
	typeMask?: "day/month/year" | "day/month";
	onChangeCustom?: (value: string) => void;
}

/**
 * Component to render a controlled input with currency mask.
 *
 * @param {string} id - Id of the input
 * @param {string} name - Name of the input
 * @param {Control<FieldValues>} control - Control of the form
 * @param {boolean} required - If true, the input will be required
 * @param {string} errorMessageCustom - Custom error message
 * @param {string} label - Label of the input
 * @param {IControllerProps} controllerProps - Props of the controller
 * @param {string} typeInput - Type of the input
 * @param {React.InputHTMLAttributes<HTMLInputElement>} customInputProps - Custom props of the input
 * @param {boolean} disabledMessage - If true, the error message will be disabled
 * @param {boolean} hideValue - If true, the value will be hidden
 * @param {Function} onChangeCustom - Function to be executed when the value changes
 * @example
 * const { control } = useForm(); //react-hoook-form
 *
 *  <DateMaskControlled
 *    id='inputtest'
 *    name="inputtest"
 *    control={control}
 *  />
 */
function DateMaskControlled<TFieldValues extends FieldValues>({
	id,
	name,
	control,
	required = false,
	errorMessageCustom,
	typeInput = "outlined",
	label = "",
	controllerProps,
	customInputProps = {},
	disabledMessage = false,
	onChangeCustom = () => {},
	hideValue = false,
	typeMask = "day/month/year",
}: Required<Pick<UseControllerProps<TFieldValues>, "name" | "control">> & IDateMaskControlledProps) {
	const isDayMonth = typeMask === "day/month";
	const isDayMonthYear = typeMask === "day/month/year";
	//#region validations
	function IsValidDate(value: string) {
		if (isDayMonth && value.length != 5) {
			return false;
		}
		if (isDayMonthYear && value.length != 10) {
			return false;
		}

		return true;
	}
	//#endregion

	const customControllerProps: IControllerProps = {
		...controllerProps,
		...{
			rules: {
				...controllerProps?.rules,
				required: required || controllerProps?.rules?.required,
				validate: {
					isValidDate: (value) => IsValidDate(value) || "",
				},
			},
		},
	};

	const useRenderInput = ({
		field,
		fieldState: { error },
	}: {
		field: ControllerRenderProps<FieldValues, string>;
		fieldState: ControllerFieldState;
		formState: UseFormStateReturn<FieldValues>;
	}) => {
		const validateValue = field.value;

		useEffect(() => {
			const NumberSlice = isDayMonth ? 5 : 10;
			const validateValueFormatted = String(validateValue).slice(0, NumberSlice);
			const haveChanges = validateValue !== validateValueFormatted || validateValue != "";
			if (haveChanges) field.onChange(validateValueFormatted);
		}, [isDayMonth]);

		const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const validateValue = dateInputMask(e.target.value, isDayMonth);

			// Notation: This is to keep the cursor in the same position
			const hasLastIndexChar = e.target.selectionStart === e.target.value.length;
			const positionChanged = hasLastIndexChar ? validateValue.length : e.target.selectionStart;
			e.target.value = validateValue;
			e.target.setSelectionRange(positionChanged, positionChanged);

			field.onChange(validateValue);
			onChangeCustom(validateValue);
		};

		const baseInputProps: IHTMLInputProps = {
			...field,
			...customInputProps,
			id: id,
			required: Boolean(customControllerProps.rules?.required),
			value: hideValue ? "" : validateValue,
			onChange: onChangeInput,
		};

		switch (typeInput) {
			case "outlined":
				return <KogInputTextOutlined {...baseInputProps} error={!!error} />;
			case "filled":
				return (
					<KogInputText
						{...baseInputProps}
						label={label}
						error={!!error}
						helperText={!disabledMessage && (errorMessageCustom || renderErrorMessages(label, error))}
					/>
				);
			default:
				return <input {...baseInputProps} />;
		}
	};

	return (
		<Controller
			name={name}
			control={control as Control<FieldValues>}
			defaultValue={""}
			{...customControllerProps}
			render={useRenderInput}
		/>
	);
}

export default DateMaskControlled;
